import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Fab,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { userTypeForm, Option } from "../../types/entitiesModel";
import dayjs, { Dayjs } from "dayjs";
import { ERROR_MESSAGE, OK_MESSAGE } from "../../utils/Constants";
import { CheckCircleOutline } from "@mui/icons-material";
import { messageType } from "../../types/otherTypes";
import { useNavigate } from "react-router-dom";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { AuthContext } from "../../context/AuthContext";


const CreateUser: React.FC = () => {
  const { token } = React.useContext(AuthContext) || {};
  const [formState, setFormState] = React.useState<userTypeForm>({
    name: "",
    email: "",
    password: "",
    role: "",
    phone: "",
    address: "",
    birthday: "",
  });

  const [message, setMessage] = React.useState<messageType>({
    message: "",
    type: OK_MESSAGE, // can change
  });

  /**
   * To handle changes on the form
   *
   * @param event event on the input
   * @param fieldName name of the field to update
   * @param fieldValue new value on the field
   */
  const handleChangeForm = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
    fieldName?: string,
    fieldValue?: string | Dayjs | null
  ) => {
    if (event) {
      const { name, value } = event.target;
      setFormState((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    } else if (fieldName && fieldValue !== undefined) {
      setFormState((prevData) => ({
        ...prevData,
        [fieldName]: fieldValue,
      }));
    }
  };

  /**
   * handle dataForDatepicker
   * @param newValue
   */
  const handleDateChange = (newValue: Dayjs | null) => {
    handleChangeForm(null, "birthday", newValue);
  };

  const options: Option[] = [
    { value: "admin", label: "Administrador" },
    { value: "user", label: "Usuario" },
  ];

  const navigate = useNavigate();

  const handleBackView = () => {
    navigate("/admin/users");
  };

  /**
   * Send data to api, to save the user
   */
  const handleSubmit = async () => {
    const requestBody = {
      name: formState.name,
      email: formState.email,
      password: formState.password,
      role: formState.role,
      phone: formState.phone,
      address: formState.address,
      birthday: dayjs(formState.birthday).format("YYYY-MM-DD"),
    };

    try {
      const response = await fetch("/api/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();

        setMessage({
          message: `Success: ${
            responseData.messages || "Data sent successfully"
          }`,
          type: OK_MESSAGE,
        });

        setTimeout(() => {
          navigate("/admin/users");
        }, 1000);
      } else {
        const errorData = await response.json();
        setMessage({
          message: `Error: ${errorData.message || "Something went wrong"}`,
          type: ERROR_MESSAGE,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage({
          message: `Error: ${error.message || "An unexpected error occurred"}`,
          type: ERROR_MESSAGE,
        });
      } else {
        setMessage({ message: "Error: Unknown error", type: ERROR_MESSAGE });
      }
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={0}>
        {message.message !== "" &&
          (message.type === OK_MESSAGE ? (
            <Alert
              icon={<CheckCircleOutline fontSize="inherit" />}
              severity="success"
              sx={{
                mx: "auto",
                mb: 2,
                width: "95%",
              }}
            >
              {message.message}
            </Alert>
          ) : (
            <Alert
              sx={{
                m: "auto",
                mb: 2,
                width: "95%",
              }}
              severity="error"
            >
              {message.message}
            </Alert>
          ))}
        <Fab
          color="primary"
          variant="extended"
          sx={{
            mr: 1,
            mb: {
              xs: 1,
              sm: 0,
              lg: 0,
            },
            ml: 2,
          }}
          onClick={handleBackView}
        >
          <KeyboardDoubleArrowLeftIcon />
          <Typography
            sx={{
              ml: 1,
              textTransform: "capitalize",
            }}
          >
            {"Tabla de usuarios"}
          </Typography>
        </Fab>
        <Grid item lg={12} md={12} xs={12}>
          <div>
            <Card
              variant="outlined"
              sx={{
                p: 0,
              }}
            >
              <Box
                sx={{
                  padding: "15px 30px",
                }}
                display="flex"
                alignItems="center"
              >
                <Box flexGrow={1}>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                  >
                    Creación de nuevo usuario
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <CardContent
                sx={{
                  padding: "30px",
                }}
              >
                <form>
                  {/* Nombre e email */}
                  <Grid container spacing={2}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="default-value"
                        label="Nombre de usuario"
                        variant="outlined"
                        placeholder="Nombre"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="name"
                        onChange={(e) => handleChangeForm(e, "name")}
                      />
                      <TextField
                        id="email"
                        label="Email"
                        type="email"
                        variant="outlined"
                        placeholder="Email"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="email"
                        onChange={(e) => handleChangeForm(e, "email")}
                      />
                    </Grid>
                    {/* Contraseña y rol de usuario */}
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="outlined-password-input"
                        label="Contraseña"
                        type="password"
                        autoComplete="Password"
                        variant="outlined"
                        placeholder="*********"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="password"
                        onChange={(e) => handleChangeForm(e, "password")}
                      />
                      <TextField
                        fullWidth
                        id="standard-select-option"
                        variant="outlined"
                        select
                        label="Rol de usuario"
                        name="role"
                        value={formState.role}
                        onChange={(e) => handleChangeForm(e, "role")}
                        sx={{ mb: 2 }}
                      >
                        {options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  {/* Dirección y teléfono */}
                  <Grid container spacing={2}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="address"
                        label="Dirección"
                        variant="outlined"
                        placeholder="Dirección"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="address"
                        onChange={(e) => handleChangeForm(e, "address")}
                      />
                      <TextField
                        id="phone"
                        label="Teléfono"
                        type="tel"
                        variant="outlined"
                        placeholder="Teléfono"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="phone"
                        onChange={(e) => handleChangeForm(e, "phone")}
                      />
                    </Grid>
                    {/* Fecha de nacimiento */}
                    <Grid item md={6} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Fecha de nacimiento"
                          value={
                            formState.birthday
                              ? dayjs(formState.birthday)
                              : null
                          }
                          onChange={handleDateChange}
                          sx={{ width: "100%" }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Button
                      onClick={() => {
                        handleSubmit();
                      }}
                      color="primary"
                      variant="contained"
                    >
                      Enviar
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </div>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default CreateUser;
